import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { X, ChevronRight, Loader2 } from "lucide-react";
import type { FacilityNode } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Drawer, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { facilityNodeIcon, FacilityIcon, CategoryIcon, ManualIcon } from "@/components/icons";
import { cn } from "@/lib/utils";
import { buildForest, flattenVisible, toggle, excludeArchived } from "./facility-tree.js";
import { useFacilities } from "./useFacilities.js";
import { useFacilityCategories } from "./useFacilityCategories.js";
import { useMoveManual, useCreateManualLink, type ManualMutationError } from "./useManualMutations.js";

export interface MoveManualDrawerProps {
  /** In MOVE mode, the manual to move; in LINK mode (`linkMode`), the SOURCE manual to link a copy of. */
  manual: { id: number; name: string };
  session: AuthSession | null;
  onClose: () => void;
  onMoved?: (manualName: string, facilityName: string, categoryName: string) => void;
  /** CREATE-A-LINK mode (ADR-020, create-and-place atomic): pick a target facility/category, then create an
   *  auto-confirmed linked COPY of `manual` there — instead of MOVING `manual`. Titles/verb/notice + the mutation switch
   *  to the link flow. (We can't create-then-move: moving a confirmed manual needs `confirm`, which a manuals_manage
   *  link-creator lacks — panel; so the create IS the placement.) */
  linkMode?: boolean;
}

/**
 * Pick a target facility + category, then either MOVE `manual` there (ADR-012) or — in `linkMode` — CREATE a linked copy
 * of `manual` there (ADR-020). A right slide-over: the caller's facility tree (a compact SELECT list) then a CATEGORY chip
 * step then a footer action. Reuses the shared tree utilities (`buildForest`/`flattenVisible`), archived facilities
 * excluded. The server enforces authz on the target (+ source); a 403/invalid surfaces inline.
 */
export function MoveManualDrawer({ manual, session, onClose, onMoved, linkMode = false }: MoveManualDrawerProps) {
  const { t } = useTranslation();
  const facilities = useFacilities(session);
  const move = useMoveManual(session);
  const createLink = useCreateManualLink(session);
  const action = linkMode ? createLink : move; // the active mutation (shared pending/error surface)
  const [target, setTarget] = useState<FacilityNode | null>(null);
  const [categoryId, setCategoryId] = useState<number | null>(null);

  const flat = useMemo(() => excludeArchived(facilities.data ?? []), [facilities.data]);
  const forest = useMemo(() => buildForest(flat), [flat]);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());
  const rows = useMemo(() => flattenVisible(forest, { isExpanded: (id) => expanded.has(id) }), [forest, expanded]);
  // Direct-child counts (the "subfacilities" number on each row — the API gives category/manual counts, not this).
  const childCount = useMemo(() => {
    const m = new Map<number, number>();
    for (const f of flat) if (f.parentId != null) m.set(f.parentId, (m.get(f.parentId) ?? 0) + 1);
    return m;
  }, [flat]);

  // The picked facility's attached categories (the step-2 chips). Fetched only once a facility is chosen.
  const categories = useFacilityCategories(session, target?.id ?? null, target !== null);
  // Reset the category choice whenever the target facility changes (its categories differ).
  useEffect(() => setCategoryId(null), [target?.id]);

  const close = () => {
    action.reset();
    onClose();
  };
  const onAction = () => {
    if (target === null || categoryId === null || action.isPending) return;
    const facilityName = target.name;
    const categoryName = categories.data?.find((c) => c.id === categoryId)?.name ?? "";
    const onSuccess = () => {
      onMoved?.(manual.name, facilityName, categoryName);
      close();
    };
    if (linkMode) createLink.mutate({ sourceId: manual.id, facilityId: target.id, categoryId }, { onSuccess });
    else move.mutate({ id: manual.id, facilityId: target.id, categoryId }, { onSuccess });
  };

  return (
    <Drawer isOpen isDismissable={!action.isPending} onOpenChange={(open) => !open && close()} className="gap-3">
      <DrawerHeader>
        <div className="min-w-0">
          <p className="text-muted-foreground text-[0.7rem] font-medium tracking-wide uppercase">
            {linkMode ? t("manualManagement.misc.createLinkedCopy") : t("manualManagement.misc.movingManual")}
          </p>
          <DrawerTitle className="truncate">{manual.name}</DrawerTitle>
        </div>
        <Button variant="ghost" size="icon" aria-label={t("manualManagement.misc.close")} onPress={close} isDisabled={action.isPending}>
          <X className="size-4" />
        </Button>
      </DrawerHeader>
      {/* Link mode leads with a realm-primary-tinted note (the placement IS the creation); move mode the plain hint. */}
      {linkMode ? (
        <p role="status" className="border-primary/30 bg-primary/5 text-foreground rounded-md border-l-2 px-3 py-2 text-sm">
          <Trans
            i18nKey="manualManagement.move.linkIntroFull"
            components={{ name: <span className="font-medium">{manual.name}</span> }}
          />
        </p>
      ) : (
        <p className="text-muted-foreground text-sm">{t("manualManagement.move.moveIntro")}</p>
      )}

      {action.isError ? (
        <p role="alert" className="text-destructive text-sm">
          {linkMode ? linkErrorCopy(action.error, t) : moveErrorCopy(action.error, t)}
        </p>
      ) : null}

      {/* Step 1 — the facility tree (compact, selectable). */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        <p className="text-muted-foreground mb-1 text-[0.7rem] font-medium tracking-wide uppercase">{t("manualManagement.move.facility")}</p>
        {facilities.isPending ? (
          <p className="text-muted-foreground flex items-center gap-2 py-4 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("common.loading")}
          </p>
        ) : facilities.isError ? (
          <div className="text-muted-foreground flex flex-col items-start gap-2 py-4 text-sm">
            <span>{t("manualManagement.move.loadFacilitiesFailed")}</span>
            <Button variant="outline" size="sm" onPress={() => void facilities.refetch()} isDisabled={facilities.isFetching}>
              {t("manualManagement.misc.tryAgain")}
            </Button>
          </div>
        ) : (
          // The SAME visual language as the main tree / facility-move drawer: a continuous timeline rail (line + node
          // dot ● branch / ○ leaf) with a white card per row (role icon · name · icon+count meta). A semantic <ul>/<li>
          // (NOT role="tree" — we don't implement the full treeitem/roving-tabindex model here; the chevron + select are
          // ordinary Tab-reachable buttons). Two sibling buttons keep the counts as valid phrasing content in the select
          // button; each count is a `role="img"` with an aria-label so a screen reader announces "2 subfacilities" (panel).
          <ul aria-label={t("manualManagement.misc.moveDestination")}>
            {rows.map((row) => {
              const { node, depth, expanded: isExpanded, hasChildren } = row;
              const Icon = facilityNodeIcon(node);
              const selected = target?.id === node.id;
              const counts = [
                { Ico: FacilityIcon, n: childCount.get(node.id) ?? 0, key: "manualManagement.move.countSubfacilities" as const },
                { Ico: CategoryIcon, n: node.categoryCount ?? 0, key: "manualManagement.move.countCategories" as const },
                { Ico: ManualIcon, n: node.manualCount ?? 0, key: "manualManagement.move.countManuals" as const },
              ].filter((c) => c.n > 0);
              return (
                <li key={node.id} className="flex items-stretch gap-1" style={{ paddingLeft: `calc(${depth} * 1.5rem)` }}>
                  {/* Timeline rail: full-height connector + the node's dot. */}
                  <div aria-hidden="true" className="relative flex w-5 shrink-0 items-center justify-center self-stretch">
                    <span className="bg-border absolute inset-y-0 left-1/2 w-px -translate-x-1/2" />
                    <span
                      className={cn(
                        "bg-background relative z-10 size-2.5 rounded-full border-2",
                        hasChildren ? "border-primary" : "border-border",
                        hasChildren && isExpanded && "bg-primary",
                      )}
                    />
                  </div>
                  {/* Facility card. */}
                  <div
                    className={cn(
                      "my-1 flex min-h-11 flex-1 items-center gap-2 rounded-lg border px-3 py-1.5 transition-all",
                      selected ? "border-primary bg-background ring-1 ring-primary" : "border-border bg-background hover:shadow-sm",
                    )}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        aria-label={t(isExpanded ? "manualManagement.move.collapse" : "manualManagement.move.expand", { name: node.name })}
                        onClick={() => setExpanded((s) => toggle(s, node.id))}
                        className="text-muted-foreground hover:text-foreground -ml-1 flex size-5 shrink-0 items-center justify-center rounded"
                      >
                        <ChevronRight className={cn("size-4 transition-transform", isExpanded && "rotate-90")} />
                      </button>
                    ) : (
                      <span className="size-5 shrink-0" aria-hidden="true" />
                    )}
                    <button type="button" aria-pressed={selected} onClick={() => setTarget(node)} className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none">
                      <Icon aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
                      <span className="block min-w-0 flex-1">
                        <span className={cn("block truncate text-sm", selected ? "font-semibold" : "font-medium")}>{node.name}</span>
                        {counts.length > 0 ? (
                          <span className="text-muted-foreground mt-0.5 flex items-center gap-2.5 text-xs">
                            {counts.map(({ Ico, n, key }, i) => (
                              <span key={i} role="img" aria-label={t(key, { count: n })} className="inline-flex shrink-0 items-center gap-1">
                                <Ico aria-hidden="true" className="size-3.5 shrink-0" />
                                {n}
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Step 2 — the picked facility's categories. */}
      {target !== null ? (
        <div className="border-border border-t pt-3">
          <p className="text-muted-foreground mb-1.5 text-[0.7rem] font-medium tracking-wide uppercase">{t("manualManagement.move.category")}</p>
          {categories.isPending ? (
            <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
          ) : categories.isError ? (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <span>{t("manualManagement.create.loadCategoriesFailed")}</span>
              <Button variant="outline" size="sm" onPress={() => void categories.refetch()} isDisabled={categories.isFetching}>
                {t("manualManagement.misc.tryAgain")}
              </Button>
            </div>
          ) : (categories.data ?? []).length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("manualManagement.move.noCategories")}</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(categories.data ?? []).map((cat) => (
                <Button
                  key={cat.id}
                  // Round pills, consistent with CreateManualDrawer: SELECTED = primary (inverts per theme),
                  // UNSELECTED = a visible muted fill. Verify both themes.
                  variant={categoryId === cat.id ? "default" : "secondary"}
                  size="sm"
                  className="rounded-full px-3"
                  aria-pressed={categoryId === cat.id}
                  onPress={() => setCategoryId(cat.id)}
                >
                  {cat.name}
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <DrawerFooter>
        <Button variant="ghost" onPress={close} isDisabled={action.isPending}>
          {t("manualManagement.misc.cancel")}
        </Button>
        <Button variant="default" onPress={onAction} isDisabled={target === null || categoryId === null || action.isPending}>
          {action.isPending ? (
            <>
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              {linkMode ? t("manualManagement.misc.creating") : t("manualManagement.misc.moving")}
            </>
          ) : linkMode ? (
            t("manualManagement.misc.createLink")
          ) : (
            t("other.moveButton")
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}

function moveErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as ManualMutationError | undefined)?.code;
  if (code === "MANUAL_FORBIDDEN") return t("manualManagement.misc.youDontHavePermissionToMove");
  if (code === "MANUAL_MOVE_TARGET_INVALID") return t("manualManagement.misc.thatCategoryIsntAvailableOnThe");
  if (code === "MANUAL_NOT_FOUND") return t("manualManagement.misc.thatManualNoLongerExistsRefresh");
  return t("manualManagement.misc.couldntMoveTheManualPleaseTry");
}

/** Inline copy for a failed create-link (ADR-020), by the contract's machine `code`. */
function linkErrorCopy(err: unknown, t: TFunction): string {
  const code = (err as ManualMutationError | undefined)?.code;
  if (code === "MANUAL_LINK_SOURCE_NOT_PUBLISHED") return t("manualManagement.misc.confirmTheSourceManualBeforeLinking");
  if (code === "MANUAL_CANNOT_LINK_A_LINK") return "A linked copy can't itself be linked.";
  if (code === "MANUAL_LINK_SOURCE_ARCHIVED") return t("manualManagement.misc.unarchiveTheSourceManualBeforeLinking");
  if (code === "CATEGORY_NOT_ATTACHED") return t("manualManagement.misc.thatCategoryIsntAvailableOnThe");
  if (code === "MANUAL_FORBIDDEN") return t("manualManagement.misc.youDontHavePermissionToCreate");
  if (code === "MANUAL_NOT_FOUND") return t("manualManagement.misc.thatManualNoLongerExistsRefresh");
  return t("manualManagement.misc.couldntCreateTheLinkPleaseTry");
}
